import { useState, useEffect, type RefCallback } from 'react';

/**
 * Lightweight viewport detection — CSS-native replacement for Framer Motion's
 * `useInView`. Returns a ref callback to attach to the target element and a
 * boolean that flips to `true` once the element enters the viewport (fires
 * once, then the IntersectionObserver disconnects automatically).
 *
 * Uses a callback ref (useState) instead of useRef so the effect re-runs the
 * moment the element mounts in the DOM — fixing the bug where components that
 * initially return null (e.g. while loading data) never set up the observer
 * because the effect had already fired with ref.current=null.
 * IntersectionObserver batches geometry reads after layout, avoiding a forced
 * layout per card/section during hydration. The homepage hero is rendered
 * immediately and does not use this entrance-animation hook.
 *
 * Usage:
 *   const { ref, inView } = useInView<HTMLDivElement>();
 *   <div ref={ref} style={{ opacity: inView ? undefined : 0 }}
 *        className={inView ? 'bk-fade-up' : undefined} />
 */
export function useInView<T extends Element = HTMLDivElement>(
  rootMargin = '0px 0px -60px 0px',
): { ref: RefCallback<T>; inView: boolean } {
  const [el, setEl]     = useState<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    // Below-fold elements: use IntersectionObserver, fire once then disconnect.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [el, rootMargin, inView]);

  return { ref: setEl, inView };
}
